import { useEffect, useState } from 'react';
import { useAppSelector } from '../../../../hooks/redux';
import {
  getCurrentStepId,
  setDisableNextStep,
  setIsDraftLoaded,
  setPreviousQuestions,
  setShowEmptyState
} from '../../../../store/slices/stepperSlice';
import { Question } from '../../../../types/AISystems.types';
import { Report } from '../../Report/Report';
import { useDispatch } from 'react-redux';
import { EmptyState } from '../../../ui/EmptyState/EmptyState';
import { Clipboard } from '../../../../icons/SvgIcons';
import { QuestionLayout } from '../QuestionLayout/QuestionLayout';

export function Stepper() {
  const {
    startAiSystemBodyResponse,
    nextQuestion,
    steps,
    previousQuestions,
    stepType,
    disableNextStep,
    loading,
    showEmptyState
  } = useAppSelector((state) => state.stepper);

  const dispatch = useDispatch();

  const [questions, setQuestions] = useState<Question[]>([]);
  const [answeredQuestionsIndexes, setAnsweredQuestionsIndexes] = useState<
    number[]
  >([]);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number | null>(
    null
  );
  const [arraysAreEqual, setArraysAreEqual] = useState(false);

  // get current step ID
  const currentStepId = getCurrentStepId(steps);

  // get first question
  useEffect(() => {
    if (startAiSystemBodyResponse?.question) {
      setQuestions([startAiSystemBodyResponse.question]);
      setAnsweredQuestionsIndexes([]);
      setActiveQuestionIndex(0);
    }
  }, [startAiSystemBodyResponse]);

  // get next question
  useEffect(() => {
    if (!nextQuestion) return;

    dispatch(setPreviousQuestions([]));

    const newQuestion = nextQuestion.question;
    const currentStep = getCurrentStepId(steps);

    // if it is a new question for the next step
    if (newQuestion.step !== currentStep) {
      setQuestions((prev) => {
        if (prev.length === 0) return prev;

        const lastIndex = prev.length - 1;
        const lastAnswered = answeredQuestionsIndexes.includes(lastIndex);

        // if the last question is not answered, delete it
        if (!lastAnswered) {
          return prev.slice(0, lastIndex);
        }

        return prev;
      });

      return; // dont display the question because it belongs to the next step
    }

    // if the question is for the current step
    if (newQuestion.step === currentStep) {
      if (questions.some((question) => question.id === newQuestion.id)) return;

      setQuestions((prev) => {
        if (prev.length === 0) return [newQuestion];

        const lastQuestion = prev[prev.length - 1];
        const lastIndex = prev.length - 1;
        const lastAnswered = answeredQuestionsIndexes.includes(lastIndex);

        if (!lastAnswered && lastQuestion.step === newQuestion.step) {
          const updated = [...prev];
          updated[lastIndex] = newQuestion;
          return updated;
        }

        if (activeQuestionIndex === null) {
          return [newQuestion];
        }

        const previousQuestions = prev.slice(0, activeQuestionIndex + 1);
        return [...previousQuestions, newQuestion];
      });

      setActiveQuestionIndex((prev) => (prev === null ? 0 : prev + 1));
    }
  }, [nextQuestion]);

  // get previous questions (step back)
  useEffect(() => {
    if (!previousQuestions || previousQuestions.length === 0) return;

    // E1 question is for pre-step
    const filteredQuestions = previousQuestions.filter(
      (question) => question.id !== 'E1'
    );

    const areQuestionsEqual =
      JSON.stringify(filteredQuestions) === JSON.stringify(questions);

    if (!areQuestionsEqual) {
      setQuestions(filteredQuestions);
      setAnsweredQuestionsIndexes(filteredQuestions.map((_, i) => i));
      setActiveQuestionIndex(filteredQuestions.length - 1);
    }
  }, [previousQuestions, questions]);

  useEffect(() => {
    if (nextQuestion?.question.step === currentStepId) {
      setQuestions([nextQuestion.question]);
      setAnsweredQuestionsIndexes([]);
      setActiveQuestionIndex(0);
    }
  }, [steps]);

  useEffect(() => {
    if (
      currentStepId !== 'REPORT' &&
      (startAiSystemBodyResponse?.type === 'result' || stepType === 'result')
    ) {
      dispatch(setShowEmptyState(true));
      dispatch(setDisableNextStep(false));
    }
  }, [steps, startAiSystemBodyResponse, currentStepId]);

  useEffect(() => {
    if (stepType !== 'result') return;

    if (!arraysAreEqual && !disableNextStep) {
      setQuestions((prev) => prev.slice(0, prev.length - 1));
    }
  }, [answeredQuestionsIndexes, stepType]);

  useEffect(() => {
    const equal = answeredQuestionsIndexes.length === questions.length;
    setArraysAreEqual(equal);

    const disableNextStep = loading || !equal;
    dispatch(setDisableNextStep(disableNextStep));
  }, [answeredQuestionsIndexes, questions, nextQuestion, loading]);

  useEffect(() => {
    dispatch(setIsDraftLoaded(false));
  }, []);

  const handleAnswered = (index: number, hasAnswer: boolean) => {
    setAnsweredQuestionsIndexes((prev) => {
      if (hasAnswer) {
        // question is answered, add to answered questions array
        return prev.includes(index) ? prev : [...prev, index];
      } else {
        // question is unanswered, remove from answered questions array
        return prev.filter((answeredIndex) => answeredIndex < index);
      }
    });

    if (!hasAnswer) {
      // if the current questions answer was cleared, remove all subsequent questions
      setQuestions((prev) => prev.slice(0, index + 1));
    }
  };

  // when an answer changes, clear all subsequent questions and mark this one as active
  const handleAnswerChange = (index: number) => {
    setQuestions((prev) => prev.slice(0, index + 1));
    setAnsweredQuestionsIndexes((prev) =>
      prev.filter((answeredIndex) => answeredIndex <= index)
    );
    setActiveQuestionIndex(index);
  };

  const disableQuestion = (index: number): boolean => {
    const nextQuestionIndex = index + 1;
    const hasNextQuesion = questions[nextQuestionIndex] !== undefined;
    const nextAnsweredQuesion =
      answeredQuestionsIndexes.includes(nextQuestionIndex);

    // disable current question only if the next question exists and has been answered
    return hasNextQuesion && nextAnsweredQuesion;
  };

  return (
    <div className="mb-6">
      {currentStepId === 'REPORT' ? (
        <Report />
      ) : showEmptyState ? (
        <EmptyState
          icon={Clipboard()}
          title="Based on your previous answers, your system does not require further assessment in this step."
          description="You can click “Save & Continue” to proceed to the next step. If you update any answers in previous step(s), questions may appear here."
          classes="border-0 flex-1"
        />
      ) : (
        questions.map((question, index) => {
          const disabled = disableQuestion(index);

          return (
            <QuestionLayout
              key={question.id}
              question={question}
              disabled={disabled}
              onAnswerStatusChange={(hasAnswer: boolean) =>
                handleAnswered(index, hasAnswer)
              }
              onAnswerChange={() => handleAnswerChange(index)}
            />
          );
        })
      )}
    </div>
  );
}
